import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { colors, radius } from '../theme';

const API = 'https://api.eighteen-media.id';

function hdr() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem('sm_token')}` } };
}

// Panel generator thumbnail/background AI yang bisa ditempel di halaman manapun.
// mode: 'thumbnail' (YouTube) atau 'ugc_bg' (background video UGC)
// projectId: UGC job tujuan yang sudah dikunci dari parent (wajib untuk bisa attach)
export default function AiGeneratorPanel({ mode, projectId, onAttached }) {
  const [artists, setArtists] = useState([]);
  const [fonts, setFonts] = useState([]);
  const [sourceMode, setSourceMode] = useState('artist');
  const [artistId, setArtistId] = useState('');
  const [referenceUrl, setReferenceUrl] = useState('');
  const [uploadingRef, setUploadingRef] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [titleText, setTitleText] = useState('');
  const [fontStyle, setFontStyle] = useState('bebas');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [attachingUrl, setAttachingUrl] = useState('');
  const [attachedUrl, setAttachedUrl] = useState('');
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/artists`, hdr()).then(r => setArtists(r.data || [])).catch(() => {});
    axios.get(`${API}/api/thumbnails/fonts`, hdr()).then(r => setFonts(r.data || [])).catch(() => {});
    fetchBalance();
  }, []);

  async function fetchBalance() {
    try {
      const r = await axios.get(`${API}/api/ai/credits`, hdr());
      setBalance(r.data.balance);
    } catch (e) {}
  }

  const selectedArtist = artists.find(a => a.id === artistId);
  const canAttach = !!projectId;

  async function uploadReference(file) {
    if (!file) return;
    setUploadingRef(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('reference', file);
      const r = await axios.post(`${API}/api/thumbnails/reference`, fd, {
        headers: { ...hdr().headers, 'Content-Type': 'multipart/form-data' },
      });
      setReferenceUrl(r.data.reference_url);
    } catch (e) {
      setError(e.response?.data?.error || 'Gagal upload foto referensi');
    }
    setUploadingRef(false);
  }

  async function generate() {
    if (sourceMode === 'artist' && !artistId) { setError('Pilih artis dulu'); return; }
    if (sourceMode === 'upload' && !referenceUrl) { setError('Upload foto referensi dulu'); return; }
    if (!prompt.trim()) { setError('Isi prompt dulu'); return; }
    setError('');
    setAttachedUrl('');
    setGenerating(true);
    setResult(null);
    try {
      const body = { prompt: prompt.trim(), font_style: fontStyle };
      if (sourceMode === 'artist') body.artist_id = artistId;
      else body.reference_url = referenceUrl;
      if (mode === 'thumbnail' && titleText.trim()) body.title_text = titleText.trim();

      const r = await axios.post(`${API}/api/thumbnails/generate`, body, hdr());
      setResult(r.data);
      if (typeof r.data.balance_remaining === 'number') setBalance(r.data.balance_remaining);
    } catch (e) {
      setError(e.response?.data?.error || 'Gagal generate');
      if (e.response?.status === 402 && typeof e.response?.data?.balance === 'number') setBalance(e.response.data.balance);
    }
    setGenerating(false);
  }

  async function addVariation() {
    if (!result?.id) return;
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/api/thumbnails/${result.id}/variation`, { font_style: fontStyle }, hdr());
      setResult(prev => ({ ...prev, generated_image_urls: r.data.generated_image_urls }));
    } catch (e) {
      setError(e.response?.data?.error || 'Gagal generate variasi (hanya tersedia untuk sumber Artis)');
    }
    setGenerating(false);
  }

  async function attachTo(url) {
    if (!canAttach) { setError('Proyek tujuan belum siap'); return; }
    setAttachingUrl(url);
    setError('');
    try {
      await axios.post(`${API}/api/thumbnails/${result.id}/attach`, {
        selected_url: url, ugc_job_id: projectId,
        target: mode === 'ugc_bg' ? 'ugc_bg' : 'thumbnail',
      }, hdr());
      setAttachedUrl(url);
      if (onAttached) onAttached(url);
    } catch (e) {
      setError(e.response?.data?.error || 'Gagal attach');
    }
    setAttachingUrl('');
  }

  async function downloadImage(url) {
    try {
      const res = await fetch(`${API}${url}`);
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = url.split('/').pop() || 'thumbnail.png';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      setError('Gagal mengunduh gambar');
    }
  }

  const insufficientBalance = balance !== null && balance < 1;

  return (
    <div>
      {balance !== null && (
        <div style={{ marginBottom: 14, padding: '8px 12px', background: insufficientBalance ? colors.dangerMuted : colors.bgCardMuted, borderRadius: 8, fontSize: 12, color: insufficientBalance ? colors.danger : colors.textSecondary, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Sisa kredit AI: <strong>{balance}</strong></span>
          {insufficientBalance && <span>Kredit kurang, hubungi admin untuk top-up</span>}
        </div>
      )}
      {error && (
        <div style={{ padding: '10px 14px', background: colors.dangerMuted, color: colors.danger, borderRadius: radius.md, marginBottom: 14, fontSize: 13 }}>{error}</div>
      )}

      <div style={{ marginBottom: 14 }}>
        <label style={{ display: 'block', fontSize: 12, color: colors.textSecondary, marginBottom: 6 }}>Sumber wajah</label>
        <div style={{ display: 'flex', gap: 8 }}>
          {[
            { id: 'artist', label: 'Pakai Foto Artis', icon: 'ti-users-group' },
            { id: 'upload', label: 'Upload Foto Sendiri', icon: 'ti-upload' },
          ].map(s => (
            <button key={s.id} onClick={() => setSourceMode(s.id)} style={{
              flex: 1, padding: '10px 0', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
              border: `1px solid ${sourceMode === s.id ? colors.accent : colors.border}`,
              background: sourceMode === s.id ? colors.accent + '22' : colors.bgCardMuted,
              color: sourceMode === s.id ? colors.accent : colors.textSecondary,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}>
              <i className={`ti ${s.icon}`} style={{ fontSize: 15 }}></i>{s.label}
            </button>
          ))}
        </div>
      </div>

      {sourceMode === 'artist' ? (
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 12, color: colors.textSecondary, marginBottom: 6 }}>Artis <span style={{ color: colors.danger }}>*</span></label>
          <select value={artistId} onChange={e => setArtistId(e.target.value)} style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.bgCardMuted, color: colors.textPrimary, fontSize: 13 }}>
            <option value="">-- Pilih artis --</option>
            {artists.filter(a => a.avatar_url).map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
          {selectedArtist?.avatar_url && (
            <img src={`${API}${selectedArtist.avatar_url}`} alt="" style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', marginTop: 8 }} />
          )}
        </div>
      ) : (
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 12, color: colors.textSecondary, marginBottom: 6 }}>Upload foto referensi <span style={{ color: colors.danger }}>*</span></label>
          <input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={e => uploadReference(e.target.files[0])} style={{ fontSize: 13, color: colors.textPrimary }} />
          {uploadingRef && <p style={{ fontSize: 12, color: colors.textSecondary, marginTop: 6 }}>Mengupload...</p>}
          {referenceUrl && !uploadingRef && (
            <img src={`${API}${referenceUrl}`} alt="" style={{ width: 60, height: 60, borderRadius: 8, objectFit: 'cover', marginTop: 8, display: 'block' }} />
          )}
        </div>
      )}

      <div style={{ marginBottom: 14 }}>
        <label style={{ display: 'block', fontSize: 12, color: colors.textSecondary, marginBottom: 6 }}>Prompt / deskripsi adegan <span style={{ color: colors.danger }}>*</span></label>
        <textarea value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="Contoh: artis berdiri di sisi kanan frame, di atas panggung konser dengan lampu neon ungu, gaya sinematik"
          style={{ width: '100%', minHeight: 70, padding: '9px 12px', borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.bgCardMuted, color: colors.textPrimary, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
      </div>

      {mode === 'thumbnail' && (
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 12, color: colors.textSecondary, marginBottom: 6 }}>Judul di thumbnail (opsional)</label>
          <input type="text" value={titleText} onChange={e => setTitleText(e.target.value)} placeholder="Contoh: Kompilasi Lagu Terbaik Shepin Mis"
            style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.bgCardMuted, color: colors.textPrimary, fontSize: 13, boxSizing: 'border-box' }} />
        </div>
      )}

      {mode === 'thumbnail' && titleText.trim() && (
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 12, color: colors.textSecondary, marginBottom: 6 }}>Gaya font</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {fonts.map(f => (
              <button key={f.key} onClick={() => setFontStyle(f.key)} style={{
                padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                border: `1px solid ${fontStyle === f.key ? colors.accent : colors.border}`,
                background: fontStyle === f.key ? colors.accent + '22' : colors.bgCardMuted,
                color: fontStyle === f.key ? colors.accent : colors.textSecondary,
              }}>{f.label}</button>
            ))}
          </div>
        </div>
      )}

      <button onClick={generate} disabled={generating || insufficientBalance} className="btn btn-gold" style={{
        width: '100%', padding: '11px 0', fontSize: 14, fontWeight: 700,
        opacity: (generating || insufficientBalance) ? 0.5 : 1, cursor: (generating || insufficientBalance) ? 'not-allowed' : 'pointer',
      }}>
        {generating ? 'Generating...' : (insufficientBalance ? 'Kredit tidak cukup' : 'Generate')}
      </button>

      {result && (
        <div style={{ marginTop: 18 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 14 }}>
            {result.generated_image_urls.map((url, i) => (
              <div key={i} style={{ border: `1px solid ${colors.border}`, borderRadius: radius.md, overflow: 'hidden' }}>
                <img src={`${API}${url}`} alt="" style={{ width: '100%', display: 'block', aspectRatio: '16/9', objectFit: 'cover' }} />
                <div style={{ padding: 8, display: 'flex', gap: 6 }}>
                  <button onClick={() => attachTo(url)} disabled={!canAttach || attachingUrl === url} style={{
                    flex: 1, padding: '8px 0', borderRadius: 6, border: 'none',
                    cursor: (!canAttach || attachingUrl === url) ? 'not-allowed' : 'pointer',
                    background: attachedUrl === url ? '#10b981' : (canAttach ? colors.accent : colors.border),
                    color: '#FFFFFF', fontSize: 12, fontWeight: 700,
                  }}>{attachingUrl === url ? '...' : (attachedUrl === url ? '✓ Terpasang' : 'Pilih & Pasang')}</button>
                  <button onClick={() => downloadImage(url)} style={{
                    width: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: 6, border: `1px solid ${colors.border}`, background: 'transparent', color: colors.textPrimary, cursor: 'pointer',
                  }}>
                    <i className="ti ti-download" style={{ fontSize: 15 }}></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
          {sourceMode === 'artist' && (
            <button onClick={addVariation} disabled={generating} style={{
              padding: '9px 16px', borderRadius: 8, border: `1px solid ${colors.borderStrong}`, background: colors.bgCard,
              color: colors.textPrimary, fontSize: 13, fontWeight: 600, cursor: generating ? 'not-allowed' : 'pointer',
            }}>{generating ? '...' : '+1 Variasi'}</button>
          )}
        </div>
      )}
    </div>
  );
}
